import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
    AlertTriangle,
    Clock,
    XCircle,
    Calendar,
    RefreshCw,
    Bell,
    Timer,
    UserX,
    TrendingUp,
    History,
    AlertCircle
} from 'lucide-react';

const AlertsPage = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [canSeeAmounts, setCanSeeAmounts] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const response = await api().get('/alerts/personnel');
            setData(response.data);
            setCanSeeAmounts(response.data.can_see_amounts || false);
            setLastUpdate(new Date());
            
            // Also trigger absence check (this sends automatic messages for absences)
            // Only do this every minute to avoid excessive calls
            try {
                await api().post('/attendance/check-absences');
            } catch (e) {
                // Silently fail - this is a background task
                console.debug('Absence check completed or skipped');
            }
        } catch (error) {
            console.error('Error fetching alerts:', error);
            toast.error('Erreur de chargement des alertes');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
        // Real-time polling every 10 seconds
        const interval = setInterval(fetchData, 10000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const formatTime = (isoString) => {
        if (!isoString) return '--:--';
        const date = new Date(isoString);
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        return date.toLocaleString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit', 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    const totalAlerts = (data?.late_alerts?.length || 0) + 
                       (data?.critical_not_clocked?.length || 0) +
                       (data?.system_alerts?.repeated_absences?.length || 0) +
                       (data?.system_alerts?.repeated_lateness?.length || 0);

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 bg-muted rounded w-48"></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-muted rounded-xl"></div>)}
                </div>
                <div className="h-64 bg-muted rounded-xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="alerts-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-display text-3xl font-bold text-foreground tracking-tight flex items-center gap-3">
                        <Bell className="w-8 h-8 text-accent" />
                        Alertes
                    </h1>
                    <p className="text-muted-foreground mt-1 flex items-center gap-2">
                        {lastUpdate && (
                            <>
                                <Clock className="w-4 h-4" />
                                Derniere mise a jour: {lastUpdate.toLocaleTimeString('fr-FR')}
                            </>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {totalAlerts > 0 && (
                        <Badge className="bg-red-500 text-white text-lg px-3 py-1">
                            {totalAlerts} alerte{totalAlerts > 1 ? 's' : ''}
                        </Badge>
                    )}
                    <Button variant="outline" size="icon" onClick={fetchData} data-testid="refresh-alerts-btn">
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className={`${data?.late_alerts?.length > 0 ? 'bg-orange-50 border-orange-200' : ''}`}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Retards en cours</p>
                                <p className={`text-2xl font-bold ${data?.late_alerts?.length > 0 ? 'text-orange-600' : ''}`}>
                                    {data?.late_alerts?.length || 0}
                                </p>
                            </div>
                            <AlertTriangle className={`w-8 h-8 ${data?.late_alerts?.length > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                        </div>
                    </CardContent>
                </Card>
                <Card className={`${data?.critical_not_clocked?.length > 0 ? 'bg-red-50 border-red-200' : ''}`}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Non pointes critiques</p>
                                <p className={`text-2xl font-bold ${data?.critical_not_clocked?.length > 0 ? 'text-red-600' : ''}`}>
                                    {data?.critical_not_clocked?.length || 0}
                                </p>
                            </div>
                            <XCircle className={`w-8 h-8 ${data?.critical_not_clocked?.length > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Modifs planning 24h</p>
                                <p className="text-2xl font-bold">{data?.planning_changes?.length || 0}</p>
                            </div>
                            <Calendar className="w-8 h-8 text-muted-foreground" />
                        </div>
                    </CardContent>
                </Card>
                <Card className={`${(data?.system_alerts?.repeated_absences?.length > 0 || data?.system_alerts?.repeated_lateness?.length > 0) ? 'bg-yellow-50 border-yellow-200' : ''}`}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Alertes systeme</p>
                                <p className="text-2xl font-bold">
                                    {(data?.system_alerts?.repeated_absences?.length || 0) + 
                                     (data?.system_alerts?.repeated_lateness?.length || 0)}
                                </p>
                            </div>
                            <AlertCircle className="w-8 h-8 text-yellow-500" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Retards en cours */}
            <Card className={`${data?.late_alerts?.length > 0 ? 'border-orange-300 shadow-orange-100' : ''}`}>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className={`w-5 h-5 ${data?.late_alerts?.length > 0 ? 'text-orange-500' : 'text-muted-foreground'}`} />
                        Retards en cours
                        {data?.late_alerts?.length > 0 && (
                            <Badge className="bg-orange-500 text-white ml-2">{data.late_alerts.length}</Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {!data?.late_alerts?.length ? (
                        <div className="text-center py-6 text-muted-foreground">
                            <AlertTriangle className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            Aucun retard en cours
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.late_alerts.map((alert, idx) => (
                                <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                                            <Timer className="w-5 h-5 text-orange-600" />
                                        </div>
                                        <div>
                                            <p className="font-medium">{alert.staff_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {alert.service} - {alert.position}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-muted-foreground">
                                            Prevu: {alert.scheduled_start} | Arrive: {formatTime(alert.check_in_time)}
                                        </p>
                                        <div className="flex items-center gap-2 justify-end mt-1">
                                            <Badge className="bg-orange-100 text-orange-700">
                                                +{alert.late_minutes} min
                                            </Badge>
                                            {/* Show amount ONLY for Direction */}
                                            {canSeeAmounts && alert.penalty_formatted && (
                                                <Badge className="bg-red-100 text-red-700 font-mono">
                                                    {alert.penalty_formatted}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Non pointes critiques */}
            <Card className={`${data?.critical_not_clocked?.length > 0 ? 'border-red-300 shadow-red-100' : ''}`}>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <UserX className={`w-5 h-5 ${data?.critical_not_clocked?.length > 0 ? 'text-red-500' : 'text-muted-foreground'}`} />
                        Non pointes critiques
                        {data?.critical_not_clocked?.length > 0 && (
                            <Badge className="bg-red-500 text-white ml-2">{data.critical_not_clocked.length}</Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {!data?.critical_not_clocked?.length ? (
                        <div className="text-center py-6 text-muted-foreground">
                            <UserX className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            Aucun employe en depassement critique
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {data.critical_not_clocked.map((alert, idx) => (
                                <div key={idx} className={`flex items-center justify-between p-3 rounded-lg border ${
                                    alert.status === 'CRITICAL' ? 'bg-red-50 border-red-300' : 'bg-yellow-50 border-yellow-200'
                                }`}>
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                            alert.status === 'CRITICAL' ? 'bg-red-100' : 'bg-yellow-100'
                                        }`}>
                                            <XCircle className={`w-5 h-5 ${
                                                alert.status === 'CRITICAL' ? 'text-red-600' : 'text-yellow-600'
                                            }`} />
                                        </div>
                                        <div>
                                            <p className="font-medium">{alert.staff_name}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {alert.service} - {alert.position}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-sm text-muted-foreground">
                                            Prevu: {alert.scheduled_start}
                                        </p>
                                        <div className="flex items-center gap-2 justify-end mt-1">
                                            <Badge className={`${
                                                alert.status === 'CRITICAL' 
                                                    ? 'bg-red-500 text-white' 
                                                    : 'bg-yellow-100 text-yellow-700'
                                            }`}>
                                                {alert.status === 'CRITICAL' ? 'CRITIQUE' : 'ATTENTION'}
                                            </Badge>
                                            <Badge variant="outline">
                                                +{alert.minutes_overdue} min sans pointage
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Planning modifie */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <History className="w-5 h-5 text-muted-foreground" />
                        Planning modifie (24h)
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {!data?.planning_changes?.length ? (
                        <div className="text-center py-6 text-muted-foreground">
                            <Calendar className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            Aucune modification recente
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {data.planning_changes.slice(0, 10).map((change, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg text-sm">
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">
                                            {change.action}
                                        </Badge>
                                        <span className="text-muted-foreground">
                                            {formatDateTime(change.timestamp)}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Alertes systeme */}
            <div className="grid md:grid-cols-2 gap-4">
                {/* Absences repetees */}
                <Card className={`${data?.system_alerts?.repeated_absences?.length > 0 ? 'border-yellow-200' : ''}`}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-yellow-500" />
                            Absences repetees ce mois
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!data?.system_alerts?.repeated_absences?.length ? (
                            <div className="text-center py-4 text-muted-foreground text-sm">
                                Aucune alerte
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {data.system_alerts.repeated_absences.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg">
                                        <div>
                                            <p className="font-medium text-sm">{item.staff_name}</p>
                                            <p className="text-xs text-muted-foreground">{item.service}</p>
                                        </div>
                                        <Badge className="bg-yellow-100 text-yellow-700">
                                            {item.absence_count} absences
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Retards frequents */}
                <Card className={`${data?.system_alerts?.repeated_lateness?.length > 0 ? 'border-orange-200' : ''}`}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-orange-500" />
                            Retards frequents ce mois
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {!data?.system_alerts?.repeated_lateness?.length ? (
                            <div className="text-center py-4 text-muted-foreground text-sm">
                                Aucune alerte
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {data.system_alerts.repeated_lateness.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                                        <div>
                                            <p className="font-medium text-sm">{item.staff_name}</p>
                                            <p className="text-xs text-muted-foreground">{item.service}</p>
                                        </div>
                                        <div className="text-right">
                                            <Badge className="bg-orange-100 text-orange-700">
                                                {item.late_count} retards
                                            </Badge>
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Total: {item.total_late_minutes} min
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default AlertsPage;
